import { useEffect } from "react";
import type { RefObject } from "react";
import { isFunction } from "lodash";

export interface UseKeyboardNavigationProps {
  elementRef?: RefObject<HTMLElement> | null;
  enabled?: boolean;
  onArrowDown: (event: KeyboardEvent) => void;
  onArrowUp: (event: KeyboardEvent) => void;
  onEnter: (event: KeyboardEvent) => void;
  onEscape: (event: KeyboardEvent) => void;
}

type EventKeyMap = Record<string, (event: KeyboardEvent) => void>;

export const useKeyboardNavigation = ({
  onArrowDown,
  onArrowUp,
  onEnter,
  onEscape,
  enabled = true,
  elementRef = null,
}: UseKeyboardNavigationProps) => {
  useEffect(() => {
    const element = elementRef?.current || document;

    const eventKeyMap: EventKeyMap = {
      ArrowDown: onArrowDown,
      ArrowUp: onArrowUp,
      Enter: onEnter,
      Escape: onEscape,
    };

    const handleKeyDown = (event: Event) => {
      if ("key" in event) {
        const keyboardEvent = event as KeyboardEvent;
        const handler = eventKeyMap[keyboardEvent.key];
        if (isFunction(handler)) {
          handler(keyboardEvent);
        }
      }
    };

    if (enabled && element) {
      element.addEventListener("keydown", handleKeyDown);
    } else {
      element?.removeEventListener("keydown", handleKeyDown);
    }

    return () => element?.removeEventListener("keydown", handleKeyDown);
  }, [enabled, onArrowDown, onArrowUp, onEnter, onEscape, elementRef]);
};
